// src/components/AdSlot.js
// Shows the highest-priority active platform ad for a placement.
// Renders nothing when there is no ad, so pages keep their layout.
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { callable } from '../firebase';

const track = (id, kind) =>
    callable('trackAdEvent')({ id, kind }).catch(() => {});

export default function AdSlot({ placement, className = '' }) {
    const [ad, setAd] = useState(null);
    const tracked = useRef(null);

    useEffect(() => {
        let alive = true;
        callable('getAds')({ placement })
            .then(({ data }) => { if (alive) setAd(data.ads?.[0] || null); })
            .catch(() => { if (alive) setAd(null); });
        return () => { alive = false; };
    }, [placement]);

    useEffect(() => {
        if (ad && tracked.current !== ad.id) {
            tracked.current = ad.id;
            track(ad.id, 'impression');
        }
    }, [ad]);

    if (!ad) return null;

    const internal = ad.ctaUrl?.startsWith('/');
    const ctaClass = 'inline-block mt-3 px-4 py-2 rounded-lg bg-green-600 text-white text-sm font-semibold hover:bg-green-700';
    const onClick = () => track(ad.id, 'click');

    return (
        <aside
            aria-label="Anúncio"
            className={`relative bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden flex flex-col sm:flex-row ${className}`}
        >
            {ad.imageUrl && (
                <img
                    src={ad.imageUrl}
                    alt=""
                    loading="lazy"
                    className="w-full sm:w-48 h-40 sm:h-auto object-cover flex-shrink-0"
                />
            )}
            <div className="p-4 sm:p-5 flex-1">
                <span className="text-[11px] uppercase tracking-wide font-semibold text-gray-400">
                    Anúncio{ad.advertiser ? ` · ${ad.advertiser}` : ''}
                </span>
                <h2 className="text-base sm:text-lg font-bold text-gray-900 mt-1">{ad.title}</h2>
                <p className="text-sm text-gray-600 mt-1">{ad.body}</p>
                {ad.ctaUrl && (internal
                    ? <Link to={ad.ctaUrl} onClick={onClick} className={ctaClass}>{ad.ctaLabel}</Link>
                    : <a href={ad.ctaUrl} onClick={onClick} target="_blank" rel="noopener noreferrer sponsored" className={ctaClass}>{ad.ctaLabel}</a>
                )}
            </div>
        </aside>
    );
}
